import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Quote } from 'lucide-react';

interface WisdomNuggetProps {
    isFocused: boolean;
}

const NUGGETS = [
    "Nature knows what your body needs before you do.",
    "The earth has medicine for every ailment—we just need to listen.",
    "Ancient remedies, modern wellness.",
    "Nature's pharmacy has no side effects, only side benefits.",
    "Let nature nurture you back to health.",
    "Your body speaks the language of plants.",
    "Small natural steps lead to big health changes.",
    "Good health grows naturally."
];

const WisdomNugget: React.FC<WisdomNuggetProps> = ({ isFocused }) => {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setIndex((prev) => (prev + 1) % NUGGETS.length);
        }, 6000); // Slower rotation
        return () => clearInterval(interval);
    }, []);

    return (
        <motion.div
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{
                opacity: isFocused ? 0 : 1,
                scale: isFocused ? 0.95 : 1,
                y: isFocused ? -20 : 0
            }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className={`
                relative max-w-lg mx-auto mb-10 p-10 rounded-full
                bg-gradient-to-br from-indigo-50/50 via-purple-50/30 to-blue-50/20
                shadow-[0_0_60px_-10px_rgba(167,139,250,0.15)]
                backdrop-blur-sm
                flex flex-col items-center text-center justify-center
                aspect-square md:aspect-auto md:min-h-[200px] md:rounded-[4rem]
                ${isFocused ? 'pointer-events-none' : ''}
            `}
        >
            {/* Decorative Elements */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <div className="bg-white p-2 rounded-full shadow-sm border border-indigo-50">
                    <Sparkles size={20} className="text-indigo-300" />
                </div>
            </div>

            <div className="flex flex-col items-center justify-center h-full w-full relative z-10">
                <span className="text-[10px] font-bold text-indigo-300 uppercase tracking-[0.2em] mb-4">Nani's Wisdom</span>

                <div className="h-24 flex items-center justify-center w-full max-w-xs md:max-w-md relative">
                    <AnimatePresence mode='wait'>
                        <motion.p
                            key={index}
                            initial={{ opacity: 0, y: 10, filter: 'blur(4px)' }}
                            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                            exit={{ opacity: 0, y: -10, filter: 'blur(4px)' }}
                            transition={{ duration: 0.8 }}
                            className="text-lg md:text-xl font-serif text-indigo-900/80 italic leading-relaxed"
                        >
                            "{NUGGETS[index]}"
                        </motion.p>
                    </AnimatePresence>
                </div>
            </div>

            {/* Soft pulsing ring */}
            <div className="absolute inset-0 rounded-[inherit] border border-indigo-100/30 animate-pulse"></div>
        </motion.div>
    );
};

export default WisdomNugget;
